import { useFeatureFlagVariantKey } from 'posthog-js/react'
import { useMemo, useState, useEffect, useCallback } from 'react'
import { FEATURE_FLAGS } from '../../hooks/featureFlags'
import { LuLightbulb, LuLoaderCircle } from 'react-icons/lu'
import { useDispatch } from 'react-redux'
import { setTitle } from '../../redux/docSlice'
import { useDocId } from '../../hooks/docID'
import { FaCheck } from 'react-icons/fa'

const TitleQuality = ({ topic }: { topic?: string }) => {
	const [isLoading, setIsLoading] = useState(false)
	const [suggestedTopic, setSuggestedTopic] = useState<string | undefined>(undefined)
	const topicValidation = useFeatureFlagVariantKey(FEATURE_FLAGS.TOPIC_VALIDATION)
	const dispatch = useDispatch()
	const docId = useDocId()

	const fetchTopicSuggestion = useCallback(async (currentTopic: string) => {
		if (!currentTopic || currentTopic?.length < 3) return

		setIsLoading(true)
		try {
			const response = await fetch(process['env']['REACT_APP_API_ROOT'] + '/essay/get-suggested-topic/', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ topic: currentTopic }),
			})
			const data = await response.json()
			if (data.topic) {
				setSuggestedTopic(data.topic)
			}
		} catch (error) {
			console.error('Error fetching topic suggestion:', error)
		} finally {
			setIsLoading(false)
		}
	}, [])

	useEffect(() => {
		if (!topicValidation || !topic) return
		setIsLoading(true)
		const timeoutId = setTimeout(() => {
			fetchTopicSuggestion(topic)
		}, 1000)

		return () => clearTimeout(timeoutId)
	}, [topic, fetchTopicSuggestion, topicValidation])

	const wordCount = useMemo(() => {
		return topic?.split(' ').filter((word) => word.length > 0).length ?? 0
	}, [topic])

	if (topicValidation === 'test') {
		if ((topic?.length ?? 0) === 0) {
			return <div className=""></div>
		}
		if ((topic?.length ?? 0) < 3) {
			return <div className="font-semibold text-danger-default">Must be at least 3 characters</div>
		}
		if (isLoading) {
			return (
				<div className=" flex items-center gap-2 text-lg">
					{LuLoaderCircle({ className: 'animate-spin' })}
					<div className="text-base text-gray-700">Processing your topic...</div>
				</div>
			)
		}
		if (suggestedTopic && (topic?.length ?? 0) < 40) {
			return (
				<div className="flex items-center gap-2 text-sm">
					{LuLightbulb({ className: 'text-link shrink-0', size: 20 })}
					<div className="text-base">
						<span className="font-semibold">Suggested Topic:</span>{' '}
						<span
							className="text-gray-800 border-b border-dashed border-b-gray-800 cursor-pointer"
							onClick={() => dispatch(setTitle({ docID: docId, title: suggestedTopic }))}
						>
							{suggestedTopic}
						</span>
					</div>
				</div>
			)
		}
		return (
			<div className="flex gap-2 items-center">
				<div className="h-5 w-5 rounded-full bg-success-default flex items-center justify-center">
					{FaCheck({ className: 'text-white', size: 12 })}
				</div>
				<div className="font-semibold">This is a good topic!</div>
			</div>
		)
	}

	return (
		<div className="flex flex-col gap-2">
			<div className="h-1 rounded bg-gray-300">
				<div
					className={`h-full rounded border-r transition-all ${
						wordCount < 10 ? (wordCount < 5 ? 'bg-danger-default' : 'bg-warning-default') : 'bg-success-default'
					}`}
					style={{ width: `${((wordCount > 10 ? 10 : wordCount) / 10) * 100}%` }}
				/>
			</div>
			{/* {wordCount > 0 && ( */}
			<div className="text-sm text-gray-700">
				{wordCount < 5 ? (
					<>
						<span className="font-semibold text-danger-default">Weak Prompt:</span> Add more context for better
						generations
					</>
				) : wordCount < 10 ? (
					<>
						<span className="font-semibold text-warning-dark">Fair Prompt:</span> Add more context for better
						generations
					</>
				) : (
					<>
						<span className="font-semibold text-success-default">Looks good!:</span> Ready for generation
					</>
				)}
			</div>
			{/* )} */}
		</div>
	)
}

export default TitleQuality
